import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmt, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Search, Ban, Check, Trash2, Pencil, Layers, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ContactHistory } from "@/components/ContactHistory";

const FIELDS = [
  { key: "phone", label: "Téléphone (E.164)" },
  { key: "first_name", label: "Prénom" },
  { key: "country", label: "Pays (optionnel)" },
  { key: "tags", label: "Tags" },
];

export default function Contacts() {
  const [multi, setMulti] = useState(null);
  const [multiBusy, setMultiBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const parseCsvText = (text) => {
    const lines = String(text).split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const sep = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
    const headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
    const find = (hints) => headers.findIndex((h) => hints.some((x) => h.includes(x)));
    const iPhone = find(["phone", "tel", "numero", "numéro", "mobile", "whatsapp"]);
    const iCountry = find(["country", "pays", "indicatif"]);
    const iName = find(["first", "prenom", "prénom"]);
    if (iPhone < 0) return [];
    return lines.slice(1).map((l) => {
      const cells = l.split(sep).map((v) => v.trim().replace(/^"|"$/g, ""));
      return {
        phone: cells[iPhone],
        country: iCountry >= 0 ? cells[iCountry] : "",
        first_name: iName >= 0 ? cells[iName] : "",
      };
    });
  };

  const onMultiFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setMultiBusy(true);
    setMulti(null);
    const CHUNK = 2000;
    const agg = { inserted: 0, duplicates: 0, invalid: 0, lists_created: 0,
      by_country: {}, files: [] };
    try {
      for (let f = 0; f < files.length; f++) {
        const file = files[f];
        const rows = parseCsvText(await file.text());
        if (!rows.length) {
          agg.files.push({ name: file.name, inserted: 0, duplicates: 0, invalid: 0,
            samples: [{ raison: "Aucune colonne téléphone trouvée" }] });
          continue;
        }
        const fileStat = { name: file.name, inserted: 0, duplicates: 0, invalid: 0, samples: [] };
        for (let start = 0; start < rows.length; start += CHUNK) {
          const chunk = rows.slice(start, start + CHUNK);
          setProgress({
            file: file.name, fileIndex: f + 1, fileTotal: files.length,
            rowsDone: Math.min(start + chunk.length, rows.length), rowsTotal: rows.length,
          });
          const { data } = await api.post("/contacts/import-multi", {
            files: [{ name: file.name, rows: chunk }],
          });
          fileStat.inserted += data.inserted;
          fileStat.duplicates += data.duplicates;
          fileStat.invalid += data.invalid;
          if (!fileStat.samples.length && data.files?.[0]?.samples?.length) {
            fileStat.samples = data.files[0].samples;
          }
          agg.inserted += data.inserted;
          agg.duplicates += data.duplicates;
          agg.invalid += data.invalid;
          Object.entries(data.by_country || {}).forEach(([k, v]) => {
            agg.by_country[k] = (agg.by_country[k] || 0) + v;
          });
        }
        agg.files.push(fileStat);
      }
      const { data: lists } = await api.get("/lists");
      agg.lists_created = lists.length;
      setMulti(agg);
      toast.success(`${agg.inserted} contact(s) importé(s) — ${lists.length} liste(s) au total`);
      load();
      loadVerif();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import interrompu — relancez pour reprendre");
      setMulti(agg);
    } finally {
      setProgress(null);
      setMultiBusy(false);
      event.target.value = "";
    }
  };

  const [verif, setVerif] = useState(null);

  const loadVerif = async () => {
    try {
      setVerif((await api.get("/verify/status")).data);
    } catch { /* non bloquant */ }
  };

  const saveVerif = async (changes) => {
    try {
      setVerif((await api.patch("/verify/settings", changes)).data);
      toast.success("Réglages de vérification enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const [data, setData] = useState({ total: 0, items: [] });
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [csv, setCsv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null);
  const [listId, setListId] = useState(new URLSearchParams(window.location.search).get("list") || "");
  const [selected, setSelected] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [targetList, setTargetList] = useState("");
  const [importList, setImportList] = useState("");
  const [forcedCountry, setForcedCountry] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", country: "", tags: "", lists: [], status: "active" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [bulk, setBulk] = useState({ add_lists: [], remove_lists: [], add_tags: "", status: "", country: "", scope: "selection" });

  const load = async () => {
    const [c, m] = await Promise.all([
      api.get("/contacts", { params: { search, status, country, list_id: listId } }),
      api.get("/contacts/meta"),
    ]);
    setData(c.data);
    setMeta(m.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, status, country, listId]);
  useEffect(() => {
    loadVerif();
    const t = setInterval(loadVerif, 15000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result).split(/\r?\n/).filter((l) => l.trim());
      const sep = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
      const headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ""));
      const rows = lines.slice(1).map((l) => l.split(sep).map((v) => v.trim().replace(/^"|"$/g, "")));
      const HINTS = {
        phone: ["phone", "tel", "telephone", "téléphone", "mobile", "numero", "numéro", "whatsapp"],
        first_name: ["first", "prenom", "prénom", "name", "nom"],
        country: ["country", "pays", "iso", "indicatif"],
        tags: ["tag", "etiquette", "étiquette", "segment"],
      };
      const guess = {};
      FIELDS.forEach((f) => {
        const idx = headers.findIndex((h) =>
          HINTS[f.key].some((hint) => h.toLowerCase().includes(hint)));
        guess[f.key] = idx >= 0 ? String(idx) : "";
      });
      setMapping(guess);
      setCsv({ headers, rows, name: file.name });
    };
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (mapping.phone === "") {
      toast.error("La colonne téléphone est obligatoire");
      return;
    }
    const rows = csv.rows.map((r) => ({
      phone: r[Number(mapping.phone)],
      first_name: mapping.first_name !== "" ? r[Number(mapping.first_name)] : "",
      country: mapping.country !== "" ? r[Number(mapping.country)] : "",
      tags: mapping.tags !== "" ? r[Number(mapping.tags)] : "",
    }));
    const { data: res } = await api.post("/contacts/import", {
      rows, list_ids: importList ? [importList] : [],
      forced_country: forcedCountry || undefined,
    });
    setReport(res);
    setCsv(null);
    toast.success(`${res.inserted} contact(s) importé(s)`);
    load();
  };

  const assign = async () => {
    if (!targetList) return;
    const { data: res } = await api.post(`/lists/${targetList}/assign`, { contact_ids: selected });
    toast.success(`${res.assigned} contact(s) assigné(s)`);
    setAssignOpen(false);
    setSelected([]);
    load();
  };

  const assignAllFiltered = async () => {
    if (!targetList) return;
    const { data: res } = await api.post(`/lists/${targetList}/assign`, {
      country: country || undefined, status: status || undefined, search: search || undefined,
    });
    toast.success(`${res.assigned} contact(s) assigné(s)`);
    setAssignOpen(false);
    setSelected([]);
    load();
  };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      first_name: c.first_name || "", country: c.country, tags: (c.tags || []).join(", "),
      lists: c.lists || [], status: c.status,
    });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/contacts/${editing.id}`, {
        first_name: editForm.first_name,
        country: editForm.country,
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        lists: editForm.lists,
        status: editForm.status,
      });
      toast.success("Contact mis à jour");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur de mise à jour");
    }
  };

  const applyBulk = async () => {
    const payload = {
      add_lists: bulk.add_lists, remove_lists: bulk.remove_lists,
      add_tags: bulk.add_tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: bulk.status || undefined, country: bulk.country || undefined,
    };
    if (bulk.scope === "selection") payload.contact_ids = selected;
    else payload.filter = { country: country || undefined, status: status || undefined, list_id: listId || undefined, search: search || undefined };
    try {
      const { data: res } = await api.post("/contacts/bulk-update", payload);
      toast.success(`${res.matched} contact(s) concerné(s)`);
      setBulkOpen(false);
      setBulk({ add_lists: [], remove_lists: [], add_tags: "", status: "", country: "", scope: "selection" });
      setSelected([]);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Modification impossible");
    }
  };

  const toggleIn = (arr, value) => (arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);

  const toggleOptout = async (c) => {
    await api.patch(`/contacts/${c.id}/status`, {
      status: c.status === "opted_out" ? "active" : "opted_out",
    });
    load();
  };

  const remove = async (c) => {
    await api.delete(`/contacts/${c.id}`);
    toast.success("Contact supprimé");
    load();
  };

  const counts = meta?.counts || {};

  return (
    <Layout
      title="Contacts"
      subtitle={`${fmt(data.total)} contact(s) — opt-in uniquement`}
      actions={
        <div className="flex flex-wrap gap-2">
          <label className={`cursor-pointer rounded-lg border border-sky-300 bg-sky-50 px-4 py-2.5 text-sm font-medium text-sky-700 hover:bg-sky-100 flex items-center gap-2 ${multiBusy ? "opacity-50" : ""}`} data-testid="multi-upload-label">
            <Upload className="h-4 w-4" />
            {multiBusy ? "Import en cours…" : "Import multi-fichiers (liste par pays)"}
            <input type="file" accept=".csv,text/csv" multiple className="hidden"
              onChange={onMultiFiles} disabled={multiBusy} data-testid="multi-file-input" />
          </label>
          <label className="cursor-pointer rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 hover:-translate-y-[1px] transition-transform duration-200 flex items-center gap-2" data-testid="csv-upload-label">
            <Upload className="h-4 w-4" /> Importer CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
          </label>
        </div>
      }
    >
      <div className="space-y-5" data-testid="contacts-page">
        {progress && (
          <div className="card-tactical p-4" data-testid="import-progress">
            <p className="text-sm text-gray-900">
              Import en cours — fichier {progress.fileIndex}/{progress.fileTotal} :{" "}
              <span className="mono">{progress.file}</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {fmt(progress.rowsDone)}/{fmt(progress.rowsTotal)} ligne(s) envoyée(s) — les gros
              fichiers sont découpés par tranches de 2 000 lignes.
            </p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-sky-500 transition-all duration-300"
                style={{ width: `${(progress.rowsDone / progress.rowsTotal) * 100}%` }} />
            </div>
          </div>
        )}
        {multi && (
          <div className="card-tactical p-4" data-testid="multi-import-report">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  Import multi-fichiers terminé — {fmt(multi.inserted)} ajouté(s) ·{" "}
                  {fmt(multi.duplicates)} doublon(s) · {fmt(multi.invalid)} invalide(s) ·{" "}
                  {multi.lists_created} liste(s) par pays
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(multi.by_country).map(([name, n]) => (
                    <span key={name} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
                      {name} · <span className="mono font-medium">{fmt(n)}</span>
                    </span>
                  ))}
                </div>
                <details className="mt-3">
                  <summary className="text-xs cursor-pointer text-gray-500">
                    Détail par fichier ({multi.files.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {multi.files.map((f) => (
                      <li key={f.name} className="text-xs text-gray-600">
                        <span className="mono">{f.name}</span> — {fmt(f.inserted)} ajouté(s),{" "}
                        {fmt(f.duplicates)} doublon(s), {fmt(f.invalid)} invalide(s)
                        {f.samples?.length > 0 && (
                          <span className="text-gray-400"> · ex. : {f.samples[0].raison}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
              <button onClick={() => setMulti(null)} data-testid="close-multi-report"
                className="text-xs text-gray-400 hover:text-gray-600 shrink-0">Fermer</button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {["active", "opted_out", "invalid", "bounced"].map((s) => (
            <div key={s} className="card-tactical p-4" data-testid={`contacts-count-${s}`}>
              <StatusBadge status={s} />
              <p className="mono text-2xl font-semibold text-gray-900 mt-2">{fmt(counts[s])}</p>
            </div>
          ))}
        </div>

        <div className="card-tactical p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              data-testid="contacts-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un numéro ou un prénom"
              className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            />
          </div>
          <select data-testid="contacts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="opted_out">Désinscrit</option>
            <option value="invalid">Invalide</option>
          </select>
          <select data-testid="contacts-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
            <option value="">Tous les pays</option>
            {(meta?.countries || []).map((c) => (
              <option key={c} value={c}>{COUNTRY_NAMES[c] || c}</option>
            ))}
          </select>
          <select data-testid="contacts-list-filter" value={listId} onChange={(e) => setListId(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
            <option value="">Toutes les listes</option>
            {(meta?.lists || []).map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>

        <div className="card-tactical p-4" data-testid="verification-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Vérification WhatsApp automatique
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Chaque contact ajouté est vérifié automatiquement, avec ou sans campagne, par une
                <strong> instance dédiée</strong> (rôle « Vérification ») et un numéro à la fois.
                Vos instances d'envoi ne sont jamais utilisées pour cela.
              </p>
              {verif && (
                <>
                  <p className="mono text-[11px] text-gray-500 mt-2" data-testid="verification-stats">
                    {verif.verifier
                      ? `Instance : ${verif.verifier.label}`
                      : "Aucune instance de vérification configurée"}
                    {" · "}{fmt(verif.pending)} en attente
                    {" · "}{fmt(verif.with_whatsapp)} avec WhatsApp
                    {" · "}{fmt(verif.invalid)} sans compte
                    {" · "}{fmt(verif.checked_today)}/{fmt(verif.settings.daily_limit)} aujourd'hui
                  </p>
                  {verif.settings.paused_reason && (
                    <p className="mt-2 text-xs text-amber-800" data-testid="verification-paused">
                      En pause : {verif.settings.paused_reason}
                    </p>
                  )}
                  {verif.warnings.map((w, i) => (
                    <p key={i} className="mt-2 text-xs text-red-700" data-testid={`verification-warning-${i}`}>
                      {w}
                    </p>
                  ))}
                  <div className="mt-3 flex flex-wrap items-end gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input type="checkbox" data-testid="verification-enabled-toggle"
                        checked={verif.settings.enabled}
                        onChange={(e) => saveVerif({ enabled: e.target.checked })} />
                      Vérification active
                    </label>
                    <div>
                      <label className="block text-[11px] text-gray-500">Intervalle (s)</label>
                      <input type="number" min={5} max={600} data-testid="verification-gap-input"
                        defaultValue={verif.settings.gap_seconds}
                        onBlur={(e) => saveVerif({ gap_seconds: Number(e.target.value) })}
                        className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm mono" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500">Plafond / jour</label>
                      <input type="number" min={10} max={1000} data-testid="verification-limit-input"
                        defaultValue={verif.settings.daily_limit}
                        onBlur={(e) => saveVerif({ daily_limit: Number(e.target.value) })}
                        className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm mono" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500">Vérifs / seconde (max 10)</label>
                      <input type="number" min={1} max={10} data-testid="verification-rate-input"
                        defaultValue={verif.settings.rate_per_second}
                        onBlur={(e) => saveVerif({ rate_per_second: Number(e.target.value) })}
                        className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm mono" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500">Taille de lot</label>
                      <input type="number" min={10} max={20000} data-testid="verification-batch-input"
                        defaultValue={verif.settings.batch_size}
                        onBlur={(e) => saveVerif({ batch_size: Number(e.target.value) })}
                        className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm mono" />
                    </div>
                    <p className="text-[11px] text-gray-400">
                      Recommandé : {verif.recommended.gap_seconds} s et{" "}
                      {verif.recommended.daily_limit}/jour
                    </p>
                  </div>
                </>
              )}
            </div>
            <button onClick={loadVerif} data-testid="verification-refresh-btn"
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50 shrink-0">
              Actualiser
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setAssignOpen(true)} data-testid="assign-to-list-btn"
            className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm hover:bg-gray-50">
            Assigner à une liste{selected.length ? ` (${selected.length})` : ""}
          </button>
          <button onClick={() => setBulkOpen(true)} data-testid="bulk-edit-btn"
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm hover:bg-gray-50">
            <Layers className="h-4 w-4" /> Édition en masse{selected.length ? ` (${selected.length})` : ""}
          </button>
          {selected.length > 0 && (
            <button onClick={() => setSelected([])} data-testid="clear-selection-btn"
              className="text-xs text-gray-500 underline">Effacer la sélection</button>
          )}
        </div>

        {report && (
          <div className="card-tactical p-4" data-testid="import-report">
            <p className="text-sm text-gray-800">
              Import terminé : <span className="mono font-medium">{report.inserted}</span> ajoutés ·{" "}
              <span className="mono">{report.duplicates}</span> doublons ·{" "}
              <span className="mono text-red-600">{report.invalid_count}</span> invalides
            </p>
            {report.invalid?.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {report.invalid.map((i, idx) => (
                  <li key={idx} className="mono text-xs text-red-600">{i.phone} — {i.raison}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="card-tactical overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" data-testid="select-all-contacts"
                    checked={selected.length > 0 && selected.length === data.items.length}
                    onChange={(e) => setSelected(e.target.checked ? data.items.map((c) => c.id) : [])} />
                </th>
                <th className="px-4 py-3">Téléphone</th>
                <th className="px-4 py-3">Prénom</th>
                <th className="px-4 py-3">Pays</th>
                <th className="px-4 py-3">Listes</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Dernier envoi</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="contacts-table-body">
              {data.items.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-3">
                    <input type="checkbox" data-testid={`select-contact-${c.id}`}
                      checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                  </td>
                  <td className="px-4 py-3 mono text-gray-900">
                    <span className="inline-flex items-center gap-1.5">
                      {c.phone}
                      {c.has_whatsapp === true && (
                        <span title="Numéro vérifié : ce contact a bien WhatsApp"
                          data-testid={`verified-check-${c.id}`}
                          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100">
                          <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3} />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">{c.first_name || "—"}</td>
                  <td className="px-4 py-3">{COUNTRY_NAMES[c.country] || c.country}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {(c.lists || []).map((id) => (meta?.lists || []).find((l) => l.id === id)?.name)
                      .filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{(c.tags || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 mono text-xs text-gray-500">{fmtTime(c.last_message_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setHistory(c)} data-testid={`history-contact-${c.id}`}
                        title="Historique" className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                        <History className="h-3.5 w-3.5 text-gray-600" />
                      </button>
                      <button onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                        <Pencil className="h-3.5 w-3.5 text-gray-600" />
                      </button>
                      <button onClick={() => toggleOptout(c)} data-testid={`optout-toggle-${c.id}`}
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-gray-100">
                        {c.status === "opted_out" ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Ban className="h-3.5 w-3.5 text-gray-500" />}
                      </button>
                      <button onClick={() => remove(c)} data-testid={`delete-contact-${c.id}`}
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-500">Aucun contact.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!csv} onOpenChange={(o) => !o && setCsv(null)}>
        <DialogContent data-testid="csv-mapping-dialog">
          <DialogHeader><DialogTitle>Correspondance des colonnes</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">{csv?.name} — {csv?.rows.length} ligne(s)</p>
          <div className="space-y-3 mt-2">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-700">{f.label}</span>
                <select
                  data-testid={`map-${f.key}`}
                  value={mapping[f.key] ?? ""}
                  onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white min-w-[180px]"
                >
                  <option value="">— ignorer —</option>
                  {csv?.headers.map((h, i) => <option key={i} value={String(i)}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 border-t border-gray-100 pt-3">
            <label className="text-sm text-gray-700">Imposer un pays à toutes les lignes (optionnel)</label>
            <select data-testid="import-forced-country-select" value={forcedCountry}
              onChange={(e) => setForcedCountry(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
              <option value="">— déduire du numéro ou de la colonne pays —</option>
              {(meta?.supported_countries || []).map((c) => (
                <option key={c} value={c}>{COUNTRY_NAMES[c] || c}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Sans pays imposé, le pays est déduit de l'indicatif du numéro (+225 → Côte d'Ivoire) ou
              de la colonne pays (code ISO, nom français ou anglais, indicatif).
            </p>
          </div>
          <div className="mt-4 space-y-2 border-t border-gray-100 pt-3">
            <label className="text-sm text-gray-700">Ajouter à une liste (optionnel)</label>
            <select data-testid="import-list-select" value={importList}
              onChange={(e) => setImportList(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
              <option value="">— aucune —</option>
              {(meta?.lists || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <button onClick={doImport} data-testid="confirm-import-btn"
            className="mt-4 w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
            Importer
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-list-dialog">
          <DialogHeader><DialogTitle>Assigner à une liste</DialogTitle></DialogHeader>
          <select data-testid="assign-list-select" value={targetList}
            onChange={(e) => setTargetList(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
            <option value="">Choisir une liste…</option>
            {(meta?.lists || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <p className="text-xs text-gray-500">
            Une liste peut contenir plusieurs pays : lors de l'envoi, l'instance est toujours choisie
            selon le pays de chaque numéro.
          </p>
          <div className="space-y-2">
            <button onClick={assign} disabled={!targetList || selected.length === 0}
              data-testid="assign-selected-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40">
              Assigner la sélection ({selected.length})
            </button>
            <button onClick={assignAllFiltered} disabled={!targetList}
              data-testid="assign-filtered-btn"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm hover:bg-gray-50 disabled:opacity-40">
              Assigner tous les contacts du filtre courant
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-contact-dialog">
          <DialogHeader><DialogTitle>Modifier {editing?.phone}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="edit-first-name-input" placeholder="Prénom" value={editForm.first_name}
              onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <select data-testid="edit-country-select" value={editForm.country}
              onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
              {(meta?.supported_countries || []).map((c) => (
                <option key={c} value={c}>{COUNTRY_NAMES[c] || c}</option>
              ))}
            </select>
            <input data-testid="edit-tags-input" placeholder="Tags séparés par des virgules" value={editForm.tags}
              onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <select data-testid="edit-status-select" value={editForm.status}
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
              <option value="active">Actif</option>
              <option value="opted_out">Désinscrit</option>
              <option value="invalid">Invalide</option>
              <option value="bounced">Rejeté</option>
            </select>
            <div>
              <p className="text-xs text-gray-500 mb-2">Listes</p>
              <div className="flex flex-wrap gap-2" data-testid="edit-lists-selector">
                {(meta?.lists || []).length === 0 && <p className="text-xs text-gray-400">Aucune liste créée.</p>}
                {(meta?.lists || []).map((l) => (
                  <button key={l.id} onClick={() => setEditForm({ ...editForm, lists: toggleIn(editForm.lists, l.id) })}
                    data-testid={`edit-list-chip-${l.id}`}
                    className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                      editForm.lists.includes(l.id)
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={saveEdit} data-testid="save-contact-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-edit-dialog">
          <DialogHeader><DialogTitle>Édition en masse</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <select data-testid="bulk-scope-select" value={bulk.scope}
              onChange={(e) => setBulk({ ...bulk, scope: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
              <option value="selection">Sélection ({selected.length} contact(s))</option>
              <option value="filter">Tous les contacts du filtre courant ({data.total})</option>
            </select>

            <div>
              <p className="text-xs text-gray-500 mb-2">Ajouter à ces listes</p>
              <div className="flex flex-wrap gap-2" data-testid="bulk-add-lists">
                {(meta?.lists || []).map((l) => (
                  <button key={l.id} onClick={() => setBulk({ ...bulk, add_lists: toggleIn(bulk.add_lists, l.id) })}
                    data-testid={`bulk-add-list-${l.id}`}
                    className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                      bulk.add_lists.includes(l.id)
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-2">Retirer de ces listes</p>
              <div className="flex flex-wrap gap-2" data-testid="bulk-remove-lists">
                {(meta?.lists || []).map((l) => (
                  <button key={l.id} onClick={() => setBulk({ ...bulk, remove_lists: toggleIn(bulk.remove_lists, l.id) })}
                    data-testid={`bulk-remove-list-${l.id}`}
                    className={`rounded-full px-3 py-1.5 text-xs border transition-colors duration-200 ${
                      bulk.remove_lists.includes(l.id)
                        ? "bg-red-500 text-white border-red-500"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>

            <input data-testid="bulk-add-tags-input" placeholder="Ajouter des tags (séparés par des virgules)"
              value={bulk.add_tags} onChange={(e) => setBulk({ ...bulk, add_tags: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />

            <div className="grid grid-cols-2 gap-3">
              <select data-testid="bulk-status-select" value={bulk.status}
                onChange={(e) => setBulk({ ...bulk, status: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
                <option value="">Statut inchangé</option>
                <option value="active">Actif</option>
                <option value="opted_out">Désinscrit</option>
                <option value="invalid">Invalide</option>
                <option value="bounced">Rejeté</option>
              </select>
              <select data-testid="bulk-country-select" value={bulk.country}
                onChange={(e) => setBulk({ ...bulk, country: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
                <option value="">Pays inchangé</option>
                {(meta?.supported_countries || []).map((c) => (
                  <option key={c} value={c}>{COUNTRY_NAMES[c] || c}</option>
                ))}
              </select>
            </div>

            <button onClick={applyBulk}
              disabled={bulk.scope === "selection" && selected.length === 0}
              data-testid="apply-bulk-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40">
              Appliquer les modifications
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <ContactHistory contact={history} onClose={() => setHistory(null)} />
    </Layout>
  );
}
